import { Icon } from '@ur-solutions/react-components'
import { useClickOutside } from '@ur-solutions/react-hooks'
import { isAfter, setYear } from 'date-fns'
import clamp from 'lodash/clamp'
import range from 'lodash/range'
import React, { useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { ZIndexRange } from '@/types/style'

interface WrapperProps {
  width?: string
  minWidth?: string
  maxWidth?: string
}
const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: ${props => props.width};
  min-width: ${props => props.minWidth};
  max-width: ${props => props.maxWidth};
  user-select: none;
`

interface DisplayProps {
  open: boolean
  light: boolean
  height: string
}
const Display = styled.div<DisplayProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.4rem;
  height: ${props => props.height};

  border: 1px solid ${props => props.theme.colors.gray9};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props =>
    props.light ? 'white' : props.theme.colors.quaternary};

  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover .arrow {
    color: ${props => props.theme.colors.secondary};
  }

  ${props =>
    props.open &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom-width: 0;

      .arrow {
        transform: translateY(-1px);
      }
    `};
`

interface CurtainProps {
  light: boolean
}
const Curtain = styled.div<CurtainProps>`
  position: absolute;
  z-index: ${ZIndexRange.Dropdowns};
  top: 100%;
  left: 0;
  right: 0;

  display: flex;
  flex-direction: column;
  padding-bottom: 0.5rem;

  background: ${props =>
    props.light ? 'white' : props.theme.colors.quaternary};
  box-shadow: 0 8px 8px -2px rgba(0, 0, 0, 0.2);

  border: solid ${props => props.theme.colors.gray9};
  border-width: 0 1px 1px 1px;
  border-bottom-left-radius: ${props => props.theme.sizes.defaultBorderRadius};
  border-bottom-right-radius: ${props => props.theme.sizes.defaultBorderRadius};
`

interface ItemProps {
  active: boolean
  inFuture?: boolean
}
const Item = styled.div<ItemProps>`
  position: relative;
  padding: 0.5rem 1.4rem;

  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: ${props =>
      props.inFuture ? props.theme.colors.gray6 : props.theme.colors.secondary};
  }

  ${props =>
    props.inFuture &&
    css`
      color: ${props.theme.colors.gray6};
    `}

  ${props =>
    props.active &&
    css`
      color: ${props.theme.colors.secondary};

      &::before {
        content: '';
        position: absolute;
        left: 0.6rem;
        transform: translateY(4px);

        display: inline-block;
        width: 6px;
        height: 6px;

        border-radius: 50%;
        background: ${props.theme.colors.secondary};
      }
    `};
`

interface YearSelectProps {
  value: Date
  /**
   * How many years into the future to show in the year select.
   * By default it the current year is in the middle of the list,
   * which means 5 future years.
   */
  futureYears?: number
  /**
   * The earliest year to which one can select, the list will have no years
   * before this.
   */
  earliestYear?: number
  /**
   * Disable click on future years from today
   */
  disableAfter?: boolean
  /**
   * Disable cycling of years on year change
   */
  noYearCycle?: boolean
  /** white background, instead of default gray */
  light?: boolean
  width?: string
  minWidth?: string
  maxWidth?: string
  fullWidth?: boolean
  height?: string
  onChange: (value: Date) => void
}

export const YearSelect: React.FC<YearSelectProps> = ({
  value,
  futureYears = 5,
  earliestYear = 2017,
  disableAfter = true,
  noYearCycle = false,
  light = false,
  width,
  minWidth,
  maxWidth,
  fullWidth = false,
  height = '3.875rem',
  onChange,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const selectedMonth = useMemo(() => value.getMonth(), [value])
  const selectedYear = useMemo(() => value.getFullYear(), [value])

  const years = useMemo(() => {
    const baseYear = noYearCycle ? new Date().getFullYear() : selectedYear
    const forwardYears =
      futureYears > 0
        ? futureYears
        : clamp(new Date().getFullYear() - baseYear, 0, 5)
    return range(
      baseYear - (baseYear - earliestYear) + 1,
      baseYear + (1 + forwardYears)
    )
  }, [futureYears, noYearCycle, selectedYear, earliestYear])

  const yearInFuture = (year: number) =>
    isAfter(new Date(year, selectedMonth, 1), new Date())

  const yearIsDisabled = (year: number) => {
    return yearInFuture(year) && disableAfter
  }

  useClickOutside(wrapperRef, () => setOpen(false), true)

  function handleYearClick(year: number) {
    if (yearIsDisabled(year)) return
    onChange(setYear(value, year))
  }

  return (
    <Wrapper
      ref={wrapperRef}
      width={fullWidth ? '100%' : width}
      minWidth={minWidth}
      maxWidth={maxWidth}
    >
      <Display
        open={open}
        light={light}
        height={height}
        onClick={() => setOpen(v => !v)}
      >
        {selectedYear}

        <Icon className="arrow" icon="chevron-down" color="gray7" size="1rem" />
      </Display>

      {open && (
        <Curtain light={light}>
          {years.map(year => (
            <Item
              key={year}
              active={year === selectedYear}
              inFuture={yearIsDisabled(year)}
              onClick={() => handleYearClick(year)}
            >
              {year}
            </Item>
          ))}
        </Curtain>
      )}
    </Wrapper>
  )
}
